import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, withLatestFrom, map } from 'rxjs/operators';

import {
  AdminRevokeAccepted,
  AdminRevokeCleared,
  CreateCPPunch,
  CreateMCPPunch,
  CreatePunchSuccess,
  DeletePunch,
  DeletePunchSuccess,
  LoadCPPunches,
  LoadMCPPunches,
  LoadPunchById,
  LoadPunchByIdSuccess,
  LoadPunchesSuccess,
  LoadSelectedAttachment,
  LoadSelectedAttachmentSuccess,
  LoadUnacceptedPunches,
  LoadUnacceptedPunchesSuccess,
  PublishAllCpPunchesToMips,
  PublishPunchToMips,
  PunchActionTypes,
  UpdatePunch,
  UpdatePunchSuccess,
  UploadAttachments,
  UploadAttachmentsSuccess,
  LoadCurrentCpCsa,
  LoadCurrentMcpCsa,
} from '@completion/actions';
import { State } from '@completion/reducers';
import { getCurrentCpId, getCurrentCsaId, getCurrentMcpId, getCurrentProject } from '@completion/selectors';
import { PunchService } from '@completion/services';
import { NotificationService } from '../../services/notification.service';
import { Attachment, Project, Punch } from '@completion/models';
import { trackResource } from './resource-fetch.util';

@Injectable()
export class PunchEffect {
  constructor(
    private readonly actions$: Actions,
    private readonly punchService: PunchService,
    private readonly store: Store<State>,
    private readonly toastr: NotificationService,
  ) {}

  loadMcpPunches$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadMCPPunches>(PunchActionTypes.LoadMCPPunches),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentMcpId) as Observable<any>),
      filter(([_, currentProject, mcpId]) => !!(currentProject && mcpId)),
      trackResource<Punch[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.punchService,
        method: 'getMcpPunches',
        serviceArgs: ([_, currentProject, mcpId]) => [currentProject.id, mcpId],
        onSuccess: (res) => new LoadPunchesSuccess(res),
      }),
    ),
  );

  loadCpPunches$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadCPPunches>(PunchActionTypes.LoadCPPunches),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      filter(([_, currentProject, cpId]) => !!(currentProject && cpId)),
      trackResource<Punch[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.punchService,
        method: 'getCpPunches',
        serviceArgs: ([_, currentProject, cpId]) => [currentProject.id, cpId],
        onSuccess: (res) => new LoadPunchesSuccess(res),
      }),
    ),
  );

  createCpPunch$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CreateCPPunch>(PunchActionTypes.CreateCPPunch),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_, currentProject, cpId, csaId]) => !!(currentProject && cpId && csaId)),
      trackResource<Punch>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.punchService,
        method: 'createCpPunch',
        // forward attachments from the action to the service
        serviceArgs: ([action, currentProject, cpId, csaId]) => [
          currentProject.id,
          cpId,
          csaId,
          action.csiNum,
          action.punch,
          (action as CreateCPPunch).attachments,
        ],
        onSuccess: (res) => new CreatePunchSuccess(res),
      }),
    ),
  );

  createMcpPunch$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CreateMCPPunch>(PunchActionTypes.CreateMCPPunch),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentMcpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_, currentProject, mcpId, csaId]) => !!(currentProject && mcpId && csaId)),
      trackResource<Punch>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.punchService,
        method: 'createMcpPunch',
        // forward attachments from the action to the service
        serviceArgs: ([action, currentProject, mcpId, csaId]) => [
          currentProject.id,
          mcpId,
          csaId,
          action.csiNum,
          action.punch,
          (action as CreateMCPPunch).attachments,
        ],
        onSuccess: (res) => new CreatePunchSuccess(res),
      }),
    ),
  );

  updatePunch$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdatePunch>(PunchActionTypes.UpdatePunch),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource<Punch>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.punchService,
        method: 'updatePunch',
        // forward attachments (if any) to the service via action.attachments
        serviceArgs: ([action, currentProject]) => [currentProject.id, (action as UpdatePunch).punch, (action as UpdatePunch).attachments],
        onSuccess: (res) => new UpdatePunchSuccess(res),
      }),
    ),
  );

  deletePunch$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeletePunch>(PunchActionTypes.DeletePunch),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.punchService,
        method: 'deletePunch',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.punchId],
        onSuccess: (res: any) => new DeletePunchSuccess(res?.status),
      }),
    ),
  );

  uploadAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UploadAttachments>(PunchActionTypes.UploadAttachments),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource<Attachment[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.punchService,
        method: 'uploadAttachments',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.punchId, action.attachments],
        onSuccess: (res) => new UploadAttachmentsSuccess(res),
      }),
    ),
  );

  loadSelectedAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadSelectedAttachment>(PunchActionTypes.LoadSelectedAttachment),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource<Blob>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.punchService,
        method: 'getAttachment',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.punchId, action.attachmentId],
        onSuccess: (res) => new LoadSelectedAttachmentSuccess(res),
      }),
    ),
  );

  loadUnacceptedPunches$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadUnacceptedPunches>(PunchActionTypes.LoadUnacceptedPunches),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource<Punch[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.punchService,
        method: 'getUnacceptedPunches',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (res) => new LoadUnacceptedPunchesSuccess(res),
      }),
    ),
  );

  adminRevokeAccepted$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AdminRevokeAccepted>(PunchActionTypes.AdminRevokeAccepted),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.punchService,
        method: 'adminRevokeAccepted',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.punchId],
        onSuccess: () => ({ type: '[Punch] Admin Revoke Accepted Success' }),
      }),
    ),
  );

  adminRevokeCleared$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AdminRevokeCleared>(PunchActionTypes.AdminRevokeCleared),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.punchService,
        method: 'adminRevokeCleared',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.punchId],
        onSuccess: () => ({ type: '[Punch] Admin Revoke Cleared Success' }),
      }),
    ),
  );

  publishPunchesToMIPS$ = createEffect(() =>
    this.actions$.pipe(
      ofType<PublishAllCpPunchesToMips>(PunchActionTypes.PublishAllCpPunchesToMips),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      filter(([_, currentProject, cpId]) => !!(currentProject && cpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.punchService,
        method: 'publishPunchesToMips',
        serviceArgs: ([_, currentProject, cpId]) => [currentProject.id, cpId],
        onSuccess: () => ({ type: '[Punch] Publish Punches To MIPS Success' }),
      }),
    ),
  );

  publishPunchToMIPS$ = createEffect(() =>
    this.actions$.pipe(
      ofType<PublishPunchToMips>(PunchActionTypes.PublishPunchToMips),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      filter(([_, currentProject, cpId]) => !!(currentProject && cpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.punchService,
        method: 'publishPunchToMips',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.punch.id],
        onSuccess: () => ({ type: '[Punch] Publish Punch To MIPS Success' }),
      }),
    ),
  );

  loadPunchById$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadPunchById>(PunchActionTypes.LoadPunchById),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource<Punch>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.punchService,
        method: 'getPunchById',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.punchId],
        onSuccess: (res) => new LoadPunchByIdSuccess(res),
      }),
    ),
  );

  // After a punch is successfully created, reload the current CSA so currentCsItems (and punch summaries) are refreshed
  reloadCsaAfterPunchCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CreatePunchSuccess>(PunchActionTypes.CreatePunchSuccess),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number>,
        this.store.select(getCurrentMcpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_, project, cpId, mcpId, csaId]) => !!(project && csaId && (cpId || mcpId))),
      map(([_, _project, _cpId, mcpId]) => (mcpId ? new LoadCurrentMcpCsa() : new LoadCurrentCpCsa())),
    ),
  );
}
